import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { Quote, BookOpen, Sparkles, Copy, Check, ZoomIn, ZoomOut, RotateCcw } from 'lucide-react';

const config = require('../../config.json');

const DEFAULT_INSIGHT = {
  message: "Everything reflects the one who alone shines. By His light all this is illuminated. In contemplation and solidarity, we discover the sacred presence transforming our human journey.",
  reference: "Katha Upanishad 5:15",
  title: "Sacred Illumination"
};

export default function TheologicalInsights() {
  const [insights, setInsights] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [copied, setCopied] = useState(false);
  const [fontOffset, setFontOffset] = useState(0);

  useEffect(() => {
    fetchActiveInfo();
  }, []);

  const generateDate = async () => {
    const d = new Date();
    const pad = (n) => n.toString().padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  };

  const todayDisplay = useMemo(() => {
    return new Date().toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short'
    });
  }, []);

  const fetchActiveInfo = async () => {
    try {
      setLoaded(false);
      const startDate = await generateDate();
      const clientID = "VJCollege";
      const searchOption = "Theological Insights";

      const response = await axios.get(
        `${config.api.invokeURL}/dashboard/infodesk?clientID=${clientID}&startDate=${startDate}&searchOption=${searchOption}`,
        {
          headers: {
            'x-api-key': config.api.key
          }
        }
      );

      if (response.data && response.data.length > 0) {
        setInsights(response.data[0]);
      }
    } catch (error) {
      console.error("Error loading theological insights:", error);
    } finally {
      setLoaded(true);
    }
  };

  const faithMsg = insights?.faithMsg;
  const rawMessage = faithMsg?.message || (!loaded ? '' : DEFAULT_INSIGHT.message);
  const title = faithMsg?.title || DEFAULT_INSIGHT.title;
  const author = faithMsg?.author || faithMsg?.reference || (!faithMsg && DEFAULT_INSIGHT.reference ? DEFAULT_INSIGHT.reference : '');

  // Dynamic Typography calculation based on text length + user adjustment
  const typography = useMemo(() => {
    if (!rawMessage) return { fontSize: '1rem', lineHeight: '1.6', isShort: false };
    const len = rawMessage.trim().length;
    let baseSize = 1.0;
    let lineHeight = 1.6;

    if (len < 90) {
      baseSize = 1.25;
      lineHeight = 1.65;
    } else if (len < 180) {
      baseSize = 1.12;
      lineHeight = 1.62;
    } else if (len < 320) {
      baseSize = 1.02;
      lineHeight = 1.58;
    } else if (len < 550) {
      baseSize = 0.94;
      lineHeight = 1.52;
    } else if (len < 850) {
      baseSize = 0.88;
      lineHeight = 1.48;
    } else {
      baseSize = 0.84;
      lineHeight = 1.45;
    }

    const finalSize = Math.max(0.76, Math.min(1.45, baseSize + fontOffset * 0.07));
    return {
      fontSize: `${finalSize.toFixed(2)}rem`,
      lineHeight: `${lineHeight}`,
      isShort: len < 180
    };
  }, [rawMessage, fontOffset]);

  const handleCopy = () => {
    if (!rawMessage) return;
    const textToCopy = `"${rawMessage}"${author ? `\n— ${author}` : ''}`;
    navigator.clipboard.writeText(textToCopy);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleZoomIn = () => setFontOffset((prev) => Math.min(3, prev + 1));
  const handleZoomOut = () => setFontOffset((prev) => Math.max(-2, prev - 1));
  const handleResetZoom = () => setFontOffset(0);

  // Split multi-line paragraphs cleanly
  const paragraphs = useMemo(() => {
    if (!rawMessage) return [];
    return rawMessage
      .split(/\n+/)
      .map((p) => p.trim())
      .filter((p) => p.length > 0);
  }, [rawMessage]);

  return (
    <div
      className="vj-modern-card vj-theological-card d-flex flex-column"
    >
      {/* Card Header */}
      <div
        className="vj-card-header py-2.5 px-3 border-bottom flex-shrink-0 d-flex align-items-center justify-content-between"
        style={{ borderColor: 'rgba(15, 28, 63, 0.06)' }}
      >
        <div className="header-left d-flex align-items-center gap-2">
          <div className="vj-icon-badge burgundy" style={{ width: '34px', height: '34px' }}>
            <Quote size={17} />
          </div>
          <div>
            <h3 className="vj-card-title mb-0" style={{ fontSize: '0.98rem', lineHeight: 1.2 }}>
              Theological Insights
            </h3>
          </div>
        </div>

        {/* Action Controls: Zoom Out / In & Copy */}
        <div className="d-flex align-items-center gap-1">
          <button
            type="button"
            className="vj-font-ctrl-btn"
            onClick={handleZoomOut}
            title="Decrease text size"
            aria-label="Decrease font size"
            disabled={fontOffset <= -2}
            style={{ opacity: fontOffset <= -2 ? 0.4 : 1 }}
          >
            <ZoomOut size={12} className="me-0.5" /> A-
          </button>
          <button
            type="button"
            className="vj-font-ctrl-btn"
            onClick={handleZoomIn}
            title="Increase text size"
            aria-label="Increase font size"
            disabled={fontOffset >= 3}
            style={{ opacity: fontOffset >= 3 ? 0.4 : 1 }}
          >
            <ZoomIn size={12} className="me-0.5" /> A+
          </button>
          {fontOffset !== 0 && (
            <button
              type="button"
              className="vj-font-ctrl-btn"
              onClick={handleResetZoom}
              title="Reset text size"
              aria-label="Reset font size"
            >
              <RotateCcw size={11} />
            </button>
          )}
          <button
            type="button"
            className="vj-font-ctrl-btn"
            onClick={handleCopy}
            title={copied ? "Copied to clipboard!" : "Copy insight text"}
            aria-label="Copy insight"
            style={{
              background: copied ? '#198754' : undefined,
              color: copied ? '#ffffff' : undefined,
              borderColor: copied ? '#198754' : undefined
            }}
          >
            {copied ? <Check size={12} /> : <Copy size={12} />}
          </button>
        </div>
      </div>

      {/* Card Body with Constrained Height & In-Card Scroll */}
      <div
        className="vj-card-body p-3 d-flex flex-column flex-grow-1"
        style={{ minHeight: 0, overflow: 'hidden' }}
      >
        {!loaded ? (
          /* Loading Skeleton State */
          <div className="vj-theological-box p-3 rounded-3 flex-grow-1 d-flex flex-column gap-2 justify-content-center">
            <div className="vj-skeleton-line" style={{ height: '14px', width: '40%' }}></div>
            <div className="vj-skeleton-line mt-2" style={{ height: '16px', width: '90%' }}></div>
            <div className="vj-skeleton-line" style={{ height: '16px', width: '100%' }}></div>
            <div className="vj-skeleton-line" style={{ height: '16px', width: '75%' }}></div>
            <div className="vj-skeleton-line mt-3" style={{ height: '12px', width: '35%' }}></div>
          </div>
        ) : (
          /* Main Theological Insight Card */
          <div
            className="vj-theological-box position-relative flex-grow-1 p-3 rounded-3 d-flex flex-column"
            style={{
              minHeight: 0,
              overflow: 'hidden'
            }}
          >
            {/* Watermark Quote Icon in Background */}
            <div
              className="position-absolute"
              style={{
                right: '-8px',
                bottom: '-12px',
                color: '#79031d',
                opacity: 0.05,
                pointerEvents: 'none',
                transform: 'rotate(8deg)'
              }}
            >
              <Quote size={110} />
            </div>

            {/* Inner Top Pill */}
            <div className="d-flex align-items-center justify-content-between mb-2 flex-shrink-0">
              <div className="d-flex align-items-center gap-1.5">
                <Sparkles size={13} style={{ color: '#79031d' }} />
                <span
                  className="fw-bold text-uppercase"
                  style={{
                    fontSize: '0.72rem',
                    color: '#79031d',
                    letterSpacing: '0.05em'
                  }}
                >
                  {title || 'Daily Reflection'}
                </span>
              </div>
              <span
                className="badge bg-light text-secondary border fw-semibold"
                style={{ fontSize: '0.68rem', padding: '3px 7px' }}
              >
                {todayDisplay}
              </span>
            </div>

            {/* In-Card Scrollable Message Section */}
            <div
              className={`vj-reflection-scroll-body flex-grow-1 pe-1 ${
                typography.isShort ? 'd-flex flex-column justify-content-center' : ''
              }`}
              style={{
                minHeight: 0,
                overflowY: 'auto'
              }}
            >
              <div
                className="vj-theological-text position-relative"
                style={{
                  fontSize: typography.fontSize,
                  lineHeight: typography.lineHeight
                }}
              >
                {paragraphs.length > 0 ? (
                  paragraphs.map((para, idx) => (
                    <p
                      key={idx}
                      className={idx === paragraphs.length - 1 ? 'mb-0' : 'mb-2.5'}
                      style={{ textAlign: 'justify' }}
                    >
                      {idx === 0 && <span style={{ color: '#79031d', fontWeight: 'bold', fontSize: '1.2em', marginRight: '2px' }}>&ldquo;</span>}
                      {para}
                      {idx === paragraphs.length - 1 && <span style={{ color: '#79031d', fontWeight: 'bold', fontSize: '1.2em', marginLeft: '2px' }}>&rdquo;</span>}
                    </p>
                  ))
                ) : (
                  <p className="mb-0 text-muted" style={{ fontStyle: 'italic' }}>
                    &ldquo;Everything reflects the one who alone shines.&rdquo;
                  </p>
                )}
              </div>

              {/* Author / Scripture Citation */}
              {author && (
                <div className="mt-3 pt-2 border-top d-flex align-items-center gap-1.5" style={{ borderColor: 'rgba(121, 3, 29, 0.12)' }}>
                  <BookOpen size={13} style={{ color: '#c69214' }} />
                  <span
                    className="fw-bold"
                    style={{
                      color: '#79031d',
                      fontSize: '0.8rem',
                      letterSpacing: '0.02em'
                    }}
                  >
                    — {author}
                  </span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Card Footer */}
        <div className="pt-2 px-1 text-end border-top mt-auto flex-shrink-0 d-flex justify-content-between align-items-center">
          <span className="text-muted fw-semibold" style={{ fontSize: '0.68rem', letterSpacing: '0.04em' }}>
            FAITH SEEKING JUSTICE
          </span>
          <span className="fw-bold" style={{ fontSize: '0.68rem', color: '#c69214', letterSpacing: '0.04em' }}>
            VIDYAJYOTI • DAILY
          </span>
        </div>
      </div>
    </div>
  );
}

